<script lang="ts" setup>
import { computed } from 'vue'
import { QBtn } from 'quasar'
import { Color } from '@previo/codebooks'
import { PSpaceSize } from '../space/types'
import { PMaxLines } from '../typography/types'
import PIcon from '../icon/PIcon.vue'
import PSpace from '../space/PSpace.vue'
import PTextCta from '../typography/links/PTextCta.vue'
import { PBtnType } from './types'

type Props = {
  text?: null | string
  type?: null | PBtnType
  iconEnd?: null | string
  color?: null | Color.PRIMARY | Color.POSITIVE | Color.NEGATIVE_STATE | Color.AI
  disable?: boolean
  loading?: boolean
  grow?: boolean
  cy: string
}

const props = withDefaults(defineProps<Props>(), {
  type: PBtnType.PRIMARY,
  color: Color.PRIMARY,
  disable: false,
  loading: false,
  grow: false,
})

type Emits = {
  (e: 'click', evt: Event): void
}

const emits = defineEmits<Emits>()

const isSecondary = computed<boolean>(() => {
  return props.type === PBtnType.SECONDARY
})

const isTertiary = computed<boolean>(() => {
  return props.type === PBtnType.TERTIARY
})

const isOutline = computed<boolean>(() => {
  return props.type === PBtnType.OUTLINE
})

const bgColor = computed<
  | null
  | Color.PRIMARY
  | Color.PRIMARY_TRANSPARENT
  | Color.POSITIVE
  | Color.POSITIVE_TRANSPARENT
  | Color.NEGATIVE_STATE
  | Color.NEGATIVE_TRANSPARENT
  | Color.GREY_300
  | Color.GREY_200
  | Color.AI
>(() => {
  if (props.disable) {
    return Color.GREY_200
  }

  if (isSecondary.value) {
    switch (props.color) {
      case Color.PRIMARY:
        return Color.PRIMARY_TRANSPARENT
      case Color.POSITIVE:
        return Color.POSITIVE_TRANSPARENT
      case Color.NEGATIVE_STATE:
        return Color.NEGATIVE_TRANSPARENT
    }
  }

  if (isOutline.value) {
    switch (props.color) {
      case Color.PRIMARY:
        return Color.GREY_300
    }
  }

  return props.color
})

const textColor = computed<
  | null
  | Color.PRIMARY
  | Color.POSITIVE
  | Color.NEGATIVE_STATE
  | Color.GREY_600
  | Color.GREY_500
  | Color.AI
>(() => {
  if (props.disable) {
    return Color.GREY_500
  }

  if (isSecondary.value) {
    return props.color
  }

  if (isOutline.value) {
    return Color.GREY_600
  }

  return null
})

const click = (e: Event): void => {
  emits('click', e)
}
</script>

<template>
  <q-btn
    :color="bgColor ?? undefined"
    :text-color="textColor ?? undefined"
    unelevated
    no-caps
    :flat="isTertiary"
    :outline="isOutline"
    :disable="disable"
    :loading="loading"
    class="ds-btn"
    :class="[{ 'ds-btn-secondary': isSecondary }, { 'p-full-width': grow }]"
    :data-cy="cy"
    no-wrap
    @click="click"
  >
    <template v-if="text">
      <p-space :mobile="PSpaceSize.SUB_HEADLINE" />
      <p-text-cta v-if="text" :text="text" :max-lines="PMaxLines.ONE" />
      <p-space v-if="text" :mobile="PSpaceSize.SUB_HEADLINE" />
    </template>
    <p-space v-if="grow && iconEnd" :mobile="PSpaceSize.GROW" />
    <template v-if="iconEnd">
      <p-icon :icon="iconEnd" />
    </template>
    <slot />
  </q-btn>
</template>

<style lang="scss" scoped>
@import '../../assets/scss/shared/mixins';
@import '../../assets/scss/shared/brand.module';

.ds-btn {
  @include mobile {
    padding: 10px;
  }

  @include laptopAndLarge {
    padding: 4px 6px 4px 6px;
  }

  &.q-btn--outline::before {
    border-color: getColor('grey200');
  }
}

.ds-btn-secondary:hover {
  box-shadow: inset 0 0 0 1px;
}
</style>
